"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { Pause, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TileLabel } from "@/components/homepage/tile";
import { useReducedMotion } from "@/lib/hooks/use-reduced-motion";
import type { GeoRegion, GeoVisit } from "@/lib/geo-visits";

/** Reserves the globe's square so the deferred canvas causes no layout shift. */
function GlobePlaceholder() {
  return <div className="size-full" aria-hidden="true" />;
}

/** Static stand-in when WebGL is unavailable (also used under E2E). Tokens only. */
function GlobeFallback() {
  return (
    <div
      data-slot="globe-fallback"
      className="flex size-full items-center justify-center"
      aria-hidden="true"
    >
      <div
        className="aspect-square w-[92%] rounded-full border border-primary/30"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, oklch(from var(--primary) l c h / 0.08), transparent 70%)",
        }}
      />
    </div>
  );
}

class GlobeErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.warn("WebGL globe failed, using static fallback:", error, info);
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

const DynamicGlobe = dynamic(
  () => import("@/components/homepage/globe-canvas").then((m) => m.GlobeCanvas),
  { ssr: false, loading: () => <GlobePlaceholder /> },
);

const ARIA_REGION_COUNT = 5;

export interface VisitorGlobeProps {
  visits: GeoVisit[];
  /** Top regions, labelled on the server so ICU differences can't cause hydration mismatches. */
  regions: GeoRegion[];
  /** Marks the panel as illustrative rather than measured (asterisk on the label). */
  estimated?: boolean;
}

export function VisitorGlobe({ visits, regions, estimated = false }: VisitorGlobeProps) {
  const headingId = useId();
  const boxRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [visible, setVisible] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [spinOverride, setSpinOverride] = useState<boolean | null>(null);
  const { shouldAnimate } = useReducedMotion();
  const spinning = spinOverride ?? shouldAnimate;

  const isE2E = process.env.NEXT_PUBLIC_E2E_TEST === "true";

  // Defer three.js until after the window load event so LCP/TBT are measured
  // against page content rather than WebGL initialisation work.
  useEffect(() => {
    const onLoad = () => setLoaded(true);
    if (document.readyState === "complete") {
      const id = setTimeout(onLoad, 0);
      return () => clearTimeout(id);
    }
    window.addEventListener("load", onLoad, { once: true });
    return () => window.removeEventListener("load", onLoad);
  }, []);

  // ...and only once the globe is near the viewport.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    if (typeof IntersectionObserver === "undefined") {
      const id = setTimeout(() => setVisible(true), 0);
      return () => clearTimeout(id);
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  const live = !isE2E && !unavailable;
  const shouldLoad = live && loaded && visible;

  const summary = regions
    .slice(0, ARIA_REGION_COUNT)
    .map((r) => `${r.label} ${r.count}`)
    .join(", ");
  const ariaLabel =
    `Globe of visitor traffic by region over the last 7 days${estimated ? ", estimated" : ""}` +
    (summary ? `: ${summary}` : "");

  return (
    <section data-slot="visitor-globe" aria-labelledby={headingId} className="flex flex-col gap-3">
      <TileLabel id={headingId}>
        visitor geo · 7d
        {estimated && <span aria-hidden="true">*</span>}
      </TileLabel>
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_16rem] items-center gap-6">
        <div ref={boxRef} className="relative mx-auto aspect-square w-full max-w-md">
          <div
            role="img"
            aria-label={ariaLabel}
            className="size-full"
          >
            {!live ? (
              <GlobeFallback />
            ) : (
              <GlobeErrorBoundary fallback={<GlobeFallback />}>
                {shouldLoad ? (
                  <DynamicGlobe
                    visits={visits}
                    spinning={spinning}
                    onInteract={() => setSpinOverride(false)}
                    onUnavailable={() => setUnavailable(true)}
                  />
                ) : (
                  <GlobePlaceholder />
                )}
              </GlobeErrorBoundary>
            )}
          </div>
          {shouldLoad && (
            // Sibling of the role="img" element: img-role descendants are
            // presentational, and nested interactive content fails axe.
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSpinOverride(!spinning)}
              className="absolute right-0 bottom-0"
            >
              {spinning ? <Pause aria-hidden="true" /> : <RotateCw aria-hidden="true" />}
              {spinning ? "pause" : "spin"}
            </Button>
          )}
        </div>
        <ol aria-label="top regions" className="flex flex-col gap-1.5">
          {regions.map((region) => (
            <li
              key={region.key}
              className="flex items-baseline justify-between gap-4 font-mono text-sm tabular-nums"
            >
              <span className="text-foreground">{region.label}</span>
              <span className="text-muted-foreground text-right">{region.count}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
